import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import dashboardService from '../../services/dashboardService';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricCard from './components/MetricCard';
import ActivityFeed from './components/ActivityFeed';
import PipelineVisualization from './components/PipelineVisualization';
import QuickActions from './components/QuickActions';
import AnalyticsChart from './components/AnalyticsChart';
import Icon from '../../components/AppIcon';
import { hasFeatureAccess, hasActionAccess } from '../../utils/roleBasedAccess';

/**
 * HR Dashboard - Role-Based Router
 * Redirects users to their appropriate role-specific dashboard
 * Fallback generic dashboard for undefined roles
 */
const HRDashboard = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const navigate = useNavigate();

  // Role-based dashboard routing
  useEffect(() => {
    const handleRoleBasedRedirect = async () => {
      console.log('[HRDashboard] Starting role-based redirect logic');
      setLoading(true);
      setError(null);
      
      try {
        // Get current user from auth service
        console.log('[HRDashboard] Getting current user');
        let user = authService.getStoredUser();
        if (user) {
          setCurrentUser(user);
          console.log('[HRDashboard] Current user loaded:', user.email, 'Role:', user.role);
        } else {
          console.log('[HRDashboard] No stored user, fetching from backend');
          const backendUser = await authService.getCurrentUser();
          setCurrentUser(backendUser);
          console.log('[HRDashboard] Current user fetched:', backendUser.email, 'Role:', backendUser.role);
          user = backendUser;
        }
        
        // Redirect based on user role
        if (user?.role) {
          setIsRedirecting(true);
          const roleRoutes = {
            'hr_manager': '/hr-roles/manager',
            'recruiter': '/hr-roles/recruiter', 
            'hr_coordinator': '/hr-roles/coordinator',
            'hr_user': '/hr-roles/coordinator' // Default hr_user to coordinator dashboard
          };
          
          const targetRoute = roleRoutes[user.role];
          if (targetRoute) {
            console.log(`[HRDashboard] Redirecting ${user.role} to ${targetRoute}`);
            navigate(targetRoute, { replace: true });
            return;
          } else {
            console.log(`[HRDashboard] Unknown role ${user.role}, showing generic dashboard`);
            // Continue to show generic dashboard for unknown roles
          }
        } else {
          console.log('[HRDashboard] No role found, showing generic dashboard');
        }
        
      } catch (error) {
        console.error('[HRDashboard] Error during role-based redirect:', error);
        setError(error.message);
      } finally {
        setLoading(false);
        setIsRedirecting(false);
      }
    };

    handleRoleBasedRedirect();
  }, [navigate]);

  // Generic dashboard fallback for unknown roles or errors
  const [dashboardData, setDashboardData] = useState(null);
  const [candidateStats, setCandidateStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Load generic dashboard data for fallback
  useEffect(() => {
    const loadGenericDashboardData = async () => {
      if (isRedirecting || !currentUser) return;
      
      console.log('[HRDashboard] Loading generic dashboard data for role:', currentUser?.role);
      
      try {
        // Load dashboard overview data
        const overview = await dashboardService.getDashboardOverview();
        setDashboardData(overview);
        console.log('[HRDashboard] Dashboard overview loaded:', overview);
        
        // Load candidate statistics
        const stats = await dashboardService.getCandidateStats();
        setCandidateStats(stats);
        console.log('[HRDashboard] Candidate stats loaded:', stats);
        
      } catch (error) {
        console.error('[HRDashboard] Error loading generic dashboard data:', error);
        setError(error.message);
      }
    };

    loadGenericDashboardData();
  }, [currentUser, isRedirecting]);

  // Real-time data updates for generic dashboard
  useEffect(() => {
    if (isRedirecting || !currentUser) return;
    
    const interval = setInterval(async () => {
      try {
        console.log('[HRDashboard] Refreshing generic dashboard data');
        const overview = await dashboardService.getDashboardOverview();
        setDashboardData(overview);
      } catch (error) {
        console.error('[HRDashboard] Error refreshing data:', error);
      }
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [currentUser, isRedirecting]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const dashboardTabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    // Analytics only for Admin and HR Manager
    ...(hasFeatureAccess(currentUser?.role, 'analytics') ? [
      { id: 'analytics', label: 'Analytics', icon: 'BarChart3' }
    ] : []),
    // Reports only for Admin and HR Manager
    ...(hasFeatureAccess(currentUser?.role, 'reports') ? [
      { id: 'reports', label: 'Reports', icon: 'FileText' }
    ] : [])
  ];

  // Calculate notification counts from real data
  const notificationCounts = {
    candidates: candidateStats?.newThisMonth || 0,
    processing: dashboardData?.recentActivity || 0,
    interviews: dashboardData?.interviewsScheduled || 0,
    total: (candidateStats?.newThisMonth || 0) + (dashboardData?.recentActivity || 0) + (dashboardData?.interviewsScheduled || 0)
  };

  // Generate metric cards from real data
  const metricCards = [
    {
      title: 'Total Applications',
      value: candidateStats?.totalCandidates?.toString() || '0',
      change: candidateStats?.newThisMonth ? `+${candidateStats.newThisMonth}` : '+0',
      changeType: 'increase',
      icon: 'FileText',
      color: 'primary'
    },
    {
      title: 'AI Screened',
      value: Math.floor((candidateStats?.totalCandidates || 0) * 0.76).toString(),
      change: '+8%',
      changeType: 'increase',
      icon: 'Brain',
      color: 'secondary'
    },
    {
      title: 'Pending Reviews',
      value: candidateStats?.byStatus?.screening?.toString() || '0',
      change: '-5%',
      changeType: 'decrease',
      icon: 'Clock',
      color: 'warning'
    },
    {
      title: 'Interviews Scheduled',
      value: candidateStats?.byStatus?.interview?.toString() || '0',
      change: '+15%',
      changeType: 'increase',
      icon: 'Calendar',
      color: 'success'
    }
  ];

  // Loading state for redirection
  if (loading || isRedirecting) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">
              {isRedirecting 
                ? `Redirecting to ${currentUser?.role || 'role-specific'} dashboard...` 
                : 'Loading dashboard...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Failed to Load Dashboard</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header 
        currentUser={currentUser}
        notificationCounts={notificationCounts}
        onNavigate={handleNavigation}
      />
      {/* Sidebar */}
      <FixedSidebar currentPath="/hr-dashboard" />
      <main className="ml-72 pt-16">
        <div className="p-6">
          {/* Breadcrumb */}
          <Breadcrumb customBreadcrumbs={[
            { label: 'Dashboard', href: '/hr-dashboard' },
            { label: 'HR Dashboard', href: '/hr-dashboard', isActive: true }
          ]} />

          {/* Dashboard Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Generic HR Dashboard</h1>
              <p className="text-muted-foreground">
                Welcome back, {currentUser?.name}. 
                {currentUser?.role ? (
                  <span className="text-amber-600 font-medium">
                    {' '}Role-specific dashboard not found for "{currentUser.role}". Showing generic view.
                  </span>
                ) : (
                  ' Here\'s your general HR overview.'
                )}
              </p>
            </div>
            <div className="flex items-center space-x-2 mt-4 lg:mt-0">
              {currentUser?.role && (
                <div className="flex items-center space-x-2 text-sm text-muted-foreground bg-amber-50 px-3 py-2 rounded-lg border border-amber-200">
                  <Icon name="AlertTriangle" size={16} className="text-amber-600" />
                  <span>Role: {currentUser.role}</span>
                </div>
              )}
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                <span>Live Updates</span>
              </div>
              <div className="text-sm text-muted-foreground">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
          </div>

          {/* Dashboard Tabs */}
          <div className="flex items-center space-x-1 mb-8 border-b border-border">
            {dashboardTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-t-lg
                  transition-all duration-200 micro-feedback
                  ${activeTab === tab.id
                    ? 'bg-primary text-primary-foreground border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Overview Tab Content */}
          {activeTab === 'overview' && (
            <>
              {/* Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                {metricCards.map((metric, index) => (
                  <MetricCard
                    key={index}
                    title={metric.title}
                    value={metric.value}
                    change={metric.change}
                    changeType={metric.changeType}
                    icon={metric.icon}
                    color={metric.color}
                  />
                ))}
              </div>

              {/* Main Dashboard Grid */}
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                {/* Activity Feed - Left Column */}
                <div className="xl:col-span-4">
                  <ActivityFeed />
                </div>

                {/* Pipeline Visualization - Center Column */}
                <div className="xl:col-span-5">
                  <PipelineVisualization />
                </div>

                {/* Quick Actions - Right Column */}
                <div className="xl:col-span-3">
                  <QuickActions />
                </div>
              </div>
            </>
          )}

          {/* Analytics Tab Content */}
          {activeTab === 'analytics' && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <div className="xl:col-span-2">
                <AnalyticsChart />
              </div>
            </div>
          )}

          {/* Reports Tab Content */}
          {activeTab === 'reports' && (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="FileText" size={32} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Reports Coming Soon</h3>
              <p className="text-muted-foreground mb-6">
                Comprehensive recruitment reports and analytics will be available in the next update.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors micro-feedback">
                  Request Early Access
                </button>
                <button className="px-6 py-2 border border-border text-foreground rounded-lg hover:bg-muted transition-colors micro-feedback">
                  Learn More
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          <footer className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-sm text-muted-foreground mb-4 md:mb-0">
                © {new Date().getFullYear()} HR AI Screening System. All rights reserved.
              </div>
              <div className="flex items-center space-x-6 text-sm text-muted-foreground">
                <button className="hover:text-foreground transition-colors">Help Center</button>
                <button className="hover:text-foreground transition-colors">Privacy Policy</button>
                <button className="hover:text-foreground transition-colors">Terms of Service</button>
              </div>
            </div>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default HRDashboard;